import { GeminiClient } from "../../eyes/utils/gemini-client.js";
import type { VideoGenerationOptions, VideoGenerationResult } from "../schemas.js";
import { logger } from "@/utils/logger.js";
import { saveBase64ToFile } from "@/utils/file-storage.js";
import type { Config } from "@/utils/config.js";
import { generateMinimaxVideo } from "../providers/minimax-video-provider.js";
import { MinimaxClient } from "@/utils/minimax-client.js";
import { generateZhipuAIVideo } from "../providers/zhipuai-video-provider.js";
import { ZhipuAIClient } from "@/utils/zhipuai-client.js";

export async function generateVideo(
  geminiClient: GeminiClient,
  options: VideoGenerationOptions,
  config?: Config
): Promise<VideoGenerationResult> {
  const startTime = Date.now();

  // Provider routing: check for Minimax provider
  const provider = (options as any).provider || config?.providers?.video || "gemini";

  if (provider === "minimax") {
    if (!config || !MinimaxClient.isConfigured(config)) {
      throw new Error("MINIMAX_API_KEY required when provider is 'minimax'");
    }

    const durationNum = parseDurationForMinimax(options.duration);

    return generateMinimaxVideo({
      prompt: options.prompt,
      model: (options as any).minimax_model || "MiniMax-Hailuo-2.3",
      duration: durationNum,
      resolution: (options as any).resolution || "1080P",
      firstFrameImage: options.imageInput,
      promptOptimizer: (options as any).prompt_optimizer ?? true,
      config,
    });
  }

  if (provider === "zhipuai") {
    if (!config || !ZhipuAIClient.isConfigured(config)) {
      throw new Error("ZHIPUAI_API_KEY required when provider is 'zhipuai'");
    }

    const durationNum = parseInt(options.duration?.replace("s", "") || "5", 10);

    return generateZhipuAIVideo({
      prompt: options.prompt,
      duration: durationNum,
      aspectRatio: options.aspectRatio,
      imageInput: options.imageInput,
      fps: options.fps,
      uploadToR2: options.uploadToR2,
      config,
    });
  }

  try {
    logger.info(`Generating video with prompt: "${options.prompt}" using model: ${options.model}`);

    const videoOptions = {
      model: options.model,
      duration: options.duration,
      aspectRatio: options.aspectRatio,
      fps: options.fps,
      imageInput: options.imageInput,
      style: options.style,
      cameraMovement: options.cameraMovement,
      seed: options.seed
    };

    const result = await geminiClient.generateVideoWithRetry(options.prompt, videoOptions);

    const generationTime = Date.now() - startTime;

    // Parse the result and return formatted response
    let resultData: string;
    let format: string;
    let filePath: string | undefined;
    let fileName: string | undefined;
    let fileUrl: string | undefined;
    let fileSize: number | undefined;

    // Determine format
    if (options.outputFormat === "mp4") {
      format = "mp4";
    } else if (options.outputFormat === "webm") {
      format = "webm";
      logger.warn("WebM format conversion not yet implemented, returning MP4");
    } else {
      format = "mp4";
    }

    // Always save file to reduce token usage, unless explicitly disabled
    const shouldSaveFile = options.saveToFile !== false;
    const shouldUploadToR2 = options.uploadToR2 === true;

    if (shouldSaveFile && config) {
      try {
        // Determine MIME type based on format
        const mimeType = format === "webm" ? "video/webm" : "video/mp4";

        const savedFile = await saveBase64ToFile(
          result.videoData,
          mimeType,
          config,
          {
            prefix: options.filePrefix || 'gemini-video',
            directory: options.saveDirectory,
            uploadToR2: shouldUploadToR2
          }
        );

        filePath = savedFile.filePath;
        fileName = savedFile.fileName;
        fileUrl = savedFile.url;
        fileSize = savedFile.size;

        logger.info(`Video saved to file: ${filePath}`);

        // Return the file path/URL instead of base64 data for better token efficiency
        resultData = fileUrl || filePath || result.videoData;
      } catch (error) {
        logger.warn(`Failed to save video file: ${error}. Falling back to base64 only.`);
        resultData = result.videoData;
      }
    } else {
      resultData = result.videoData;
    }

    return {
      videoData: resultData,
      format,
      model: options.model,
      duration: options.duration,
      aspectRatio: options.aspectRatio,
      fps: options.fps,
      generationTime,
      size: estimateVideoSize(options.duration, options.aspectRatio),
      operationId: result.operationId,
      filePath,
      fileName,
      fileUrl,
      fileSize
    };

  } catch (error) {
    const generationTime = Date.now() - startTime;
    logger.error(`Video generation failed after ${generationTime}ms:`, error);

    if (error instanceof Error) {
      if (error.message.includes("API key")) {
        throw new Error("Invalid or missing Google AI API key. Please check your GOOGLE_GEMINI_API_KEY environment variable.");
      }
      if (error.message.includes("quota") || error.message.includes("rate limit")) {
        throw new Error("API quota exceeded or rate limit reached. Please try again later.");
      }
      if (error.message.includes("safety") || error.message.includes("policy")) {
        throw new Error("Video generation blocked due to safety policies. Please modify your prompt and try again.");
      }
      if (error.message.includes("timeout")) {
        throw new Error("Video generation timed out. This is normal for longer videos. Please try again or use a shorter duration.");
      }
      throw new Error(`Video generation failed: ${error.message}`);
    }

    throw new Error("Video generation failed due to an unexpected error");
  }
}

export async function generateImageToVideo(
  geminiClient: GeminiClient,
  prompt: string,
  imageInput: string,
  options: Partial<VideoGenerationOptions> = {},
  config?: Config
): Promise<VideoGenerationResult> {
  logger.info(`Generating video from image with prompt: "${prompt}"`);

  const videoOptions: VideoGenerationOptions = {
    prompt,
    model: options.model || "veo-3.0-generate-001",
    duration: options.duration || "4s",
    outputFormat: options.outputFormat || "mp4",
    aspectRatio: options.aspectRatio || "16:9",
    fps: options.fps || 24,
    imageInput,
    style: options.style,
    cameraMovement: options.cameraMovement,
    seed: options.seed,
    fetchTimeout: options.fetchTimeout || 300000
  };

  return await generateVideo(geminiClient, videoOptions, config);
}

export async function pollVideoGeneration(
  geminiClient: GeminiClient,
  operationId: string,
  maxWaitTime: number = 300000 // 5 minutes
): Promise<VideoGenerationResult> {
  const startTime = Date.now();
  const pollInterval = 5000; // 5 seconds

  logger.info(`Polling video generation operation: ${operationId}`);

  while (Date.now() - startTime < maxWaitTime) {
    try {
      const status = await geminiClient.pollVideoGenerationOperation(operationId);

      if (status.done) {
        if (status.error) {
          throw new Error(`Video generation failed: ${status.error}`);
        }

        if (status.result) {
          const generationTime = Date.now() - startTime;
          logger.info(`Video generation completed in ${generationTime}ms`);

          return {
            videoData: status.result.videoData,
            format: "mp4",
            model: "veo-3.0-generate-001",
            duration: "4s", // Would come from operation metadata
            aspectRatio: "16:9", // Would come from operation metadata
            fps: 24, // Would come from operation metadata
            generationTime: status.result.generationTime,
            size: "1920x1080", // Would be calculated from actual video
            operationId
          };
        }
      }

      // Wait before next poll
      await new Promise(resolve => setTimeout(resolve, pollInterval));
      logger.debug(`Video generation still in progress... (${Math.floor((Date.now() - startTime) / 1000)}s elapsed)`);

    } catch (error) {
      logger.error(`Error polling video generation:`, error);
      throw new Error(`Failed to poll video generation: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  throw new Error(`Video generation timed out after ${maxWaitTime / 1000} seconds`);
}

/** Convert Gemini-style duration string (e.g. "4s") to Minimax-compatible number (6 or 10) */
function parseDurationForMinimax(duration: string): number {
  const seconds = parseInt(duration.replace("s", ""));
  if (seconds <= 6) return 6;
  return 10;
}

function estimateVideoSize(duration: string, aspectRatio: string): string {
  // Estimate video dimensions based on duration and aspect ratio
  const durationSeconds = parseInt(duration.replace('s', ''));

  let width: number, height: number;

  switch (aspectRatio) {
    case "1:1":
      width = 1024; height = 1024;
      break;
    case "16:9":
      width = 1920; height = 1080;
      break;
    case "9:16":
      width = 1080; height = 1920;
      break;
    case "4:3":
      width = 1440; height = 1080;
      break;
    case "3:4":
      width = 1080; height = 1440;
      break;
    default:
      width = 1920; height = 1080;
  }

  return `${width}x${height}`;
}